import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAdminProducts,
  updateProductPrice,
  updateProductLicenseRequirement,
  bulkUpdatePrices,
  fetchProductStats
} from '../../features/products/productsSlice';
// import './AdminProducts.css'; // Commenté car les classes Tailwind suffisent maintenant

const AdminProducts = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('list');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceFilter, setPriceFilter] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [priceUpdates, setPriceUpdates] = useState({});
  const [bulkUpdateMode, setBulkUpdateMode] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [updateReason, setUpdateReason] = useState('');
  const [quickActions, setQuickActions] = useState({
    discountPercent: 10,
    increasePercent: 5
  });

  // Redux state
  const { products, categories, stats, loading: productsLoading, error } = useSelector(
    (state) => {
      console.log('🔍 Redux state - products:', state.products);
      console.log('🔍 Redux state - auth:', state.auth);
      return state.products;
    }
  );
  
  const { userInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    console.log('🔄 AdminProducts: Chargement initial...');
    console.log('👤 UserInfo:', userInfo);
    console.log('🔐 Token:', userInfo?.token);
    console.log('👑 IsAdmin:', userInfo?.isAdmin);
    
    if (userInfo && userInfo.token && userInfo.isAdmin) {
      loadProducts();
      loadStats();
    } else {
      console.error('❌ Utilisateur non authentifié ou non admin');
    }
  }, [userInfo]); // Dépendance sur userInfo

  useEffect(() => {
    // Recharger seulement quand les filtres changent vraiment
    if (searchTerm !== '' || selectedCategory !== '') {
      console.log('🔄 AdminProducts: Filtres changés, rechargement...');
      loadProducts();
    }
  }, [searchTerm, selectedCategory]);

  const getAdminProductsParams = () => ({
    search: searchTerm,
    category: selectedCategory,
    page: 1,
    limit: 50,
  });

  const loadProducts = () => {
    console.log('📦 AdminProducts: loadProducts appelé avec:', {
      search: searchTerm,
      category: selectedCategory,
    });

    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    console.log('👤 UserInfo depuis localStorage:', userInfo);
    console.log('🔐 Token présent:', !!userInfo.token);
    console.log('👑 Est admin:', userInfo.isAdmin);

    void dispatch(fetchAdminProducts(getAdminProductsParams()))
      .unwrap()
      .catch((err) => console.error('AdminProducts loadProducts:', err));
  };

  const reloadProductsAwait = () =>
    dispatch(fetchAdminProducts(getAdminProductsParams())).unwrap();

  const loadStats = () => {
    console.log('📊 AdminProducts: Chargement des statistiques...');
    dispatch(fetchProductStats());
  };

  const handleLicenseRequirementChange = async (product, requiresLicense) => {
    const current = product.requiresLicense !== false;
    if (current === requiresLicense) return;
    try {
      setLoading(true);
      await dispatch(
        updateProductLicenseRequirement({
          productId: String(product._id),
          requiresLicense,
        })
      ).unwrap();
      await reloadProductsAwait();
    } catch (e) {
      console.error(e);
      const msg =
        typeof e === 'string'
          ? e
          : e?.message ||
            (e && typeof e === 'object' && 'error' in e ? String(e.error) : null) ||
            'Impossible de mettre à jour la licence (réseau, droits admin ou API non déployée).';
      window.alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handlePriceUpdate = async (productId, newPrice, reason) => {
    try {
      setLoading(true);
      console.log('🔄 Mise à jour du prix:', { productId, newPrice, reason });
      
      await dispatch(updateProductPrice({ 
        productId, 
        price: newPrice, 
        reason 
      }));
      
      setShowPriceModal(false);
      setSelectedProduct(null);
      setNewPrice('');
      setUpdateReason('');
      loadProducts(); // Recharger la liste
      console.log('✅ Prix mis à jour avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour du prix:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    try {
      setLoading(true);
      const updates = Object.entries(priceUpdates).map(([productId, data]) => ({
        productId,
        price: parseFloat(data.price),
        reason: data.reason || 'Mise à jour groupée'
      }));

      await dispatch(bulkUpdatePrices(updates));
      setPriceUpdates({});
      setBulkUpdateMode(false);
      loadProducts();
    } catch (error) {
      console.error('Erreur lors de la mise à jour groupée:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPriceModal = (product) => {
    setSelectedProduct(product);
    setNewPrice(product.price.toString());
    setUpdateReason('');
    setShowPriceModal(true);
  };

  const formatPrice = (price) => `${price.toFixed(2)}€`;

  const calculateChange = (oldPrice, newPrice) => {
    const diff = newPrice - oldPrice;
    const percent = oldPrice > 0 ? ((diff / oldPrice) * 100).toFixed(1) : '0.0';
    return { diff, percent, isIncrease: diff > 0 };
  };

  // Actions rapides pour les prix
  const applyQuickDiscount = (product, percent) => {
    const newPrice = (product.price * (1 - percent / 100)).toFixed(2);
    setPriceUpdates({
      ...priceUpdates,
      [product._id]: {
        price: newPrice,
        reason: `Réduction de ${percent}%`
      }
    });
  };

  const applyQuickIncrease = (product, percent) => {
    const newPrice = (product.price * (1 + percent / 100)).toFixed(2);
    setPriceUpdates({
      ...priceUpdates,
      [product._id]: {
        price: newPrice,
        reason: `Augmentation de ${percent}%`
      }
    });
  };

  const setRoundPrice = (product) => {
    const roundedPrice = Math.round(product.price);
    setPriceUpdates({
      ...priceUpdates,
      [product._id]: {
        price: roundedPrice.toString(),
        reason: 'Arrondi au prix entier'
      }
    });
  };

  return (
    <div className="space-y-6 admin-products-container">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-6 rounded-lg shadow border border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Produits</h2>
          {/* Debug info */}
          <div className="text-xs text-gray-500 mt-1">
            Produits chargés: {products?.length || 0} |
            Catégories: {categories?.length || 0} |
            Loading: {productsLoading ? 'Oui' : 'Non'}
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setBulkUpdateMode(!bulkUpdateMode)}
            className={`px-4 py-2 rounded-md text-sm font-medium border ${
              bulkUpdateMode
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300 border-gray-300'
            }`}
          >
            {bulkUpdateMode ? 'Annuler' : 'Mise à jour groupée'}
          </button>
          <button
            onClick={loadProducts}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 border border-blue-600"
          >
            Actualiser
          </button>
          <button
            onClick={() => {
              console.log('🔍 DEBUG - État Redux:', {
                products,
                categories,
                stats,
                loading: productsLoading,
                error
              });
              alert(`Debug info logged to console`);
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 border border-purple-600"
          >
            Debug
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          <p className="font-medium">Erreur:</p>
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      {/* Vérification des permissions */}
      {!userInfo || !userInfo.isAdmin ? (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
          <p className="font-medium">Accès refusé</p>
          <p className="text-sm">Vous devez être connecté en tant qu'administrateur pour accéder à cette page.</p>
        </div>
      ) : (
        <>
          {/* Tabs */}
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-md">
                <span className="text-blue-600 text-lg">📦</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Produits</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalProducts}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-md">
                <span className="text-green-600 text-lg">💰</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Revenus Totaux</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalRevenue?.toFixed(2)}€</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-md">
                <span className="text-purple-600 text-lg">🛒</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Ventes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSales}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-md">
                <span className="text-yellow-600 text-lg">📊</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Panier Moyen</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalSales > 0 ? (stats.totalRevenue / stats.totalSales).toFixed(2) : '0.00'}€
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Filters for Indicators */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm font-medium text-gray-700">Filtres rapides :</span>
          <button
            onClick={() => {
              setSelectedCategory('');
              setSearchTerm('');
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              !selectedCategory && !searchTerm
                ? 'bg-blue-100 text-blue-800 border-blue-200'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
          >
            📦 Tous les produits
          </button>
          <button
            onClick={() => {
              setSelectedCategory('Volume');
              setSearchTerm('');
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              selectedCategory === 'Volume'
                ? 'bg-purple-100 text-purple-800 border-purple-200'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
          >
            📊 Indicateurs Volume
          </button>
          <button
            onClick={() => {
              setSelectedCategory('Trend');
              setSearchTerm('');
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              selectedCategory === 'Trend'
                ? 'bg-green-100 text-green-800 border-green-200'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
          >
            📈 Indicateurs Trend
          </button>
          <button
            onClick={() => {
              setSelectedCategory('Support');
              setSearchTerm('');
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              selectedCategory === 'Support'
                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
          >
            🎯 Indicateurs Support
          </button>
          <button
            onClick={() => {
              setSelectedCategory('Volatility');
              setSearchTerm('');
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              selectedCategory === 'Volatility'
                ? 'bg-red-100 text-red-800 border-red-200'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
          >
            ⚡ Indicateurs Volatilité
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recherche
            </label>
            <input
              type="text"
              placeholder="Rechercher par nom ou slug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Catégorie
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            >
              <option value="">Toutes les catégories</option>
              {categories?.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prix min (€)
            </label>
            <input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={priceFilter.min}
              onChange={(e) => setPriceFilter({...priceFilter, min: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prix max (€)
            </label>
            <input
              type="number"
              step="0.01"
              placeholder="999.99"
              value={priceFilter.max}
              onChange={(e) => setPriceFilter({...priceFilter, max: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trier par
            </label>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            >
              <option value="name-asc">Nom (A-Z)</option>
              <option value="name-desc">Nom (Z-A)</option>
              <option value="price-asc">Prix (↑)</option>
              <option value="price-desc">Prix (↓)</option>
              <option value="category-asc">Catégorie (A-Z)</option>
            </select>
          </div>
        </div>

        {/* Actions rapides en mode bulk */}
        {bulkUpdateMode && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Actions rapides</h4>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  products?.forEach(product => {
                    if (!priceUpdates[product._id]) {
                      applyQuickDiscount(product, quickActions.discountPercent);
                    }
                  });
                }}
                className="px-3 py-1 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200"
              >
                -{quickActions.discountPercent}% sur tous
              </button>
              <button
                onClick={() => {
                  products?.forEach(product => {
                    if (!priceUpdates[product._id]) {
                      applyQuickIncrease(product, quickActions.increasePercent);
                    }
                  });
                }}
                className="px-3 py-1 bg-green-100 text-green-800 rounded text-xs font-medium hover:bg-green-200"
              >
                +{quickActions.increasePercent}% sur tous
              </button>
              <button
                onClick={() => {
                  products?.forEach(product => {
                    if (!priceUpdates[product._id] && product.price % 1 !== 0) {
                      setRoundPrice(product);
                    }
                  });
                }}
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium hover:bg-blue-200"
              >
                Arrondir tous
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        {productsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Chargement des produits...</p>
          </div>
        ) : !products || products.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun produit trouvé</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || selectedCategory
                ? "Aucun produit ne correspond à vos critères de recherche."
                : "Il n'y a encore aucun produit dans la base de données."
              }
            </p>
            <div className="space-x-3">
              <button
                onClick={loadProducts}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 border border-blue-600"
              >
                Actualiser
              </button>
              {(searchTerm || selectedCategory) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md text-sm font-medium hover:bg-gray-300 border border-gray-300"
                >
                  Effacer les filtres
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Catégorie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Prix Actuel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Licence NT
                  </th>
                  {bulkUpdateMode && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Nouveau Prix
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {console.log('📋 Rendu des produits:', products) || products?.map((product) => (
                  <tr key={product._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {product.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {product.slug}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        product.category === 'Volume' ? 'bg-purple-100 text-purple-800' :
                        product.category === 'Trend' ? 'bg-green-100 text-green-800' :
                        product.category === 'Support' ? 'bg-yellow-100 text-yellow-800' :
                        product.category === 'Volatility' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {product.category === 'Volume' ? '📊' :
                         product.category === 'Trend' ? '📈' :
                         product.category === 'Support' ? '🎯' :
                         product.category === 'Volatility' ? '⚡' :
                         '📦'} {product.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatPrice(product.price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                      <select
                        value={product.requiresLicense === false ? 'no' : 'yes'}
                        onChange={(e) =>
                          handleLicenseRequirementChange(
                            product,
                            e.target.value === 'yes'
                          )
                        }
                        disabled={loading}
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white"
                        title="Demande de licence côté site / NinjaTrader"
                      >
                        <option value="yes">Oui</option>
                        <option value="no">Non</option>
                      </select>
                    </td>
                    {bulkUpdateMode && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          step="0.01"
                          placeholder={product.price}
                          value={priceUpdates[product._id]?.price || ''}
                          onChange={(e) =>
                            setPriceUpdates({
                              ...priceUpdates,
                              [product._id]: {
                                ...priceUpdates[product._id],
                                price: e.target.value,
                                reason: priceUpdates[product._id]?.reason || ''
                              }
                            })
                          }
                          className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm text-gray-900 bg-white"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {!bulkUpdateMode ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openPriceModal(product)}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            Modifier
                          </button>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => {
                                setBulkUpdateMode(true);
                                applyQuickDiscount(product, 10);
                              }}
                              className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200"
                              title="Réduction 10%"
                            >
                              -10%
                            </button>
                            <button
                              onClick={() => {
                                setBulkUpdateMode(true);
                                applyQuickIncrease(product, 5);
                              }}
                              className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200"
                              title="Augmentation 5%"
                            >
                              +5%
                            </button>
                            {product.price % 1 !== 0 && (
                              <button
                                onClick={() => {
                                  setBulkUpdateMode(true);
                                  setRoundPrice(product);
                                }}
                                className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                                title="Arrondir"
                              >
                                ≈
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            placeholder="Raison..."
                            value={priceUpdates[product._id]?.reason || ''}
                            onChange={(e) =>
                              setPriceUpdates({
                                ...priceUpdates,
                                [product._id]: {
                                  ...priceUpdates[product._id],
                                  price: priceUpdates[product._id]?.price || product.price,
                                  reason: e.target.value
                                }
                              })
                            }
                            className="w-32 px-2 py-1 border border-gray-300 rounded-md text-xs text-gray-900 bg-white"
                          />
                          {priceUpdates[product._id]?.price && (
                            <span className={`text-xs font-medium ${
                              calculateChange(product.price, parseFloat(priceUpdates[product._id].price)).isIncrease
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}>
                              {calculateChange(product.price, parseFloat(priceUpdates[product._id].price)).isIncrease ? '+' : ''}
                              {calculateChange(product.price, parseFloat(priceUpdates[product._id].price)).percent}%
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk Update Actions */}
      {bulkUpdateMode && (
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Mise à jour groupée</h3>
              <p className="text-sm text-gray-600">
                {Object.keys(priceUpdates).length} produit(s) à mettre à jour
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setPriceUpdates({})}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md text-sm font-medium hover:bg-gray-400 border border-gray-300"
              >
                Vider
              </button>
              <button
                onClick={handleBulkUpdate}
                disabled={loading || Object.keys(priceUpdates).length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 border border-green-600"
              >
                {loading ? 'Mise à jour...' : 'Appliquer les changements'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Price Update Modal */}
      {showPriceModal && selectedProduct && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-gray-300 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Modifier le prix - {selectedProduct.name}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix actuel
                  </label>
                  <p className="text-lg font-bold text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                    {formatPrice(selectedProduct.price)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nouveau prix (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    placeholder="0.00"
                  />
                </div>

                {newPrice && parseFloat(newPrice) !== selectedProduct.price && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800 font-medium">
                      Changement: {calculateChange(selectedProduct.price, parseFloat(newPrice)).isIncrease ? '+' : ''}
                      {formatPrice(Math.abs(parseFloat(newPrice) - selectedProduct.price))}
                      ({calculateChange(selectedProduct.price, parseFloat(newPrice)).isIncrease ? '+' : ''}
                      {calculateChange(selectedProduct.price, parseFloat(newPrice)).percent}%)
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Raison de la modification
                  </label>
                  <textarea
                    value={updateReason}
                    onChange={(e) => setUpdateReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    rows="3"
                    placeholder="Promotion saisonnière, ajustement tarifaire, etc."
                  />
                </div>

                {/* Actions rapides dans la modal */}
                <div className="bg-gray-50 p-3 rounded-md">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Actions rapides
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const discountPrice = (selectedProduct.price * 0.9).toFixed(2);
                        setNewPrice(discountPrice);
                        setUpdateReason('Réduction de 10%');
                      }}
                      className="px-3 py-1 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200"
                    >
                      -10% ({formatPrice(selectedProduct.price * 0.9)})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const discountPrice = (selectedProduct.price * 0.8).toFixed(2);
                        setNewPrice(discountPrice);
                        setUpdateReason('Réduction de 20%');
                      }}
                      className="px-3 py-1 bg-red-100 text-red-800 rounded text-xs font-medium hover:bg-red-200"
                    >
                      -20% ({formatPrice(selectedProduct.price * 0.8)})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const increasePrice = (selectedProduct.price * 1.05).toFixed(2);
                        setNewPrice(increasePrice);
                        setUpdateReason('Augmentation de 5%');
                      }}
                      className="px-3 py-1 bg-green-100 text-green-800 rounded text-xs font-medium hover:bg-green-200"
                    >
                      +5% ({formatPrice(selectedProduct.price * 1.05)})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const roundedPrice = Math.round(selectedProduct.price).toString();
                        setNewPrice(roundedPrice);
                        setUpdateReason('Arrondi au prix entier');
                      }}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium hover:bg-blue-200"
                    >
                      Arrondir ({Math.round(selectedProduct.price)}€)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setNewPrice('1.00');
                        setUpdateReason('Prix promotionnel indicateur');
                      }}
                      className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium hover:bg-yellow-200"
                    >
                      📊 1€ promo
                    </button>
                    {/* Actions spécifiques aux indicateurs */}
                    {(selectedProduct.category === 'Volume' || selectedProduct.category === 'Trend' || 
                      selectedProduct.category === 'Support' || selectedProduct.category === 'Volatility') && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const premiumPrice = Math.max(99, selectedProduct.price * 0.7).toFixed(2);
                            setNewPrice(premiumPrice);
                            setUpdateReason('Prix premium indicateur');
                          }}
                          className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium hover:bg-indigo-200"
                        >
                          💎 Premium (99€+)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const standardPrice = Math.max(49, selectedProduct.price * 0.5).toFixed(2);
                            setNewPrice(standardPrice);
                            setUpdateReason('Prix standard indicateur');
                          }}
                          className="px-3 py-1 bg-cyan-100 text-cyan-800 rounded text-xs font-medium hover:bg-cyan-200"
                        >
                          ⭐ Standard (49€+)
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowPriceModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md text-sm font-medium hover:bg-gray-400 border border-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    console.log('🔍 selectedProduct:', selectedProduct);
                    console.log('🔍 selectedProduct._id:', selectedProduct?._id);
                    if (selectedProduct?._id) {
                      handlePriceUpdate(selectedProduct._id, parseFloat(newPrice), updateReason);
                    } else {
                      console.error('❌ selectedProduct ou _id manquant:', selectedProduct);
                    }
                  }}
                  disabled={loading || !newPrice || parseFloat(newPrice) < 0 || !selectedProduct?._id}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 border border-blue-600"
                >
                  {loading ? 'Mise à jour...' : 'Mettre à jour'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default AdminProducts;
